import {
  adminDownloadTemplatePayslip,
  adminUploadPayslip,
} from "helpers/backend_helpers";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

const UploadPayslip = ({ show, onCloseClick, getData }: any) => {
  const [file, setFile] = useState<File | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [displayCount, setDisplayCount] = useState(5);

  const uploadData = async () => {
    if (!file) {
      toast.error("Pilih file terlebih dahulu");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setIsLoading(true);
    await adminUploadPayslip(formData)
      .then((response: any) => {
        setDetailData(response);
        getData();

        if (response.countInvalid === 0) {
          toast.success(`Berhasil upload ${response.countValid} slip gaji`);
        } else {
          toast.warning(
            `Upload selesai: ${response.countValid} berhasil, ${response.countInvalid} gagal`,
          );
        }
      })
      .catch((error) => {
        toast.error("Gagal upload data");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const templateExcel = () => {
    setIsLoading(true);
    adminDownloadTemplatePayslip()
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "template_upload_slip_gaji.xlsx");
        document.body.appendChild(link);
        link.click();
      })
      .catch(() => {
        toast.error("Gagal download template");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setDetailData(null);
    }
  };

  const handleClose = () => {
    setFile(null);
    setDetailData(null);
    setDisplayCount(5);
    onCloseClick();
  };

  const handleShowMore = () => {
    setDisplayCount((prev) => prev + 5);
  };

  const getDisplayedData = () => {
    if (!detailData?.notes) return [];
    return detailData.notes.slice(0, displayCount);
  };

  const hasMoreData = () => {
    if (!detailData?.notes) return false;
    return detailData.notes.length > displayCount;
  };

  useEffect(() => {
    if (!show) {
      setFile(null);
      setDetailData(null);
      setDisplayCount(5);
    }
  }, [show]);

  return (
    <Modal
      fade={true}
      isOpen={show}
      toggle={handleClose}
      centered={true}
      size="lg"
    >
      <ModalHeader className="bg-light p-3" toggle={handleClose}>
        Upload Slip Gaji
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col lg={12}>
            <div className="mb-3">
              <Label htmlFor="file-input" className="form-label">
                Pilih File Excel
              </Label>
              <Input
                type="file"
                className="form-control"
                id="file-input"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
              />
              <div className="form-text">
                Format file: .xlsx atau .xls |{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    templateExcel();
                  }}
                  className="text-decoration-underline"
                >
                  Download Template
                </a>
              </div>
            </div>

            {file && (
              <Alert color="info" className="mb-3">
                <i className="ri-file-excel-2-line align-middle me-2"></i>
                File terpilih: <strong>{file.name}</strong>
              </Alert>
            )}

            {detailData && (
              <div className="mt-3">
                <Alert
                  color={detailData.countInvalid === 0 ? "success" : "warning"}
                  className="border-0"
                >
                  <h5 className="alert-heading mb-3">
                    <i className="ri-information-line align-middle me-2"></i>
                    Hasil Upload
                  </h5>
                  <hr />
                  <Row className="g-3">
                    <Col sm={6}>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="avatar-sm">
                            <div className="avatar-title bg-success-subtle text-success rounded fs-3">
                              <i className="ri-checkbox-circle-fill"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Data Valid</h6>
                          <p className="text-muted mb-0 fs-4 fw-semibold">
                            {detailData.countValid || 0}
                          </p>
                        </div>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="d-flex align-items-center">
                        <div className="flex-shrink-0">
                          <div className="avatar-sm">
                            <div className="avatar-title bg-danger-subtle text-danger rounded fs-3">
                              <i className="ri-close-circle-fill"></i>
                            </div>
                          </div>
                        </div>
                        <div className="flex-grow-1 ms-3">
                          <h6 className="mb-1">Data Invalid</h6>
                          <p className="text-muted mb-0 fs-4 fw-semibold">
                            {detailData.countInvalid || 0}
                          </p>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {detailData.countInvalid > 0 && (
                    <div className="mt-3">
                      <p className="mb-0 text-muted">
                        <i className="ri-information-line align-middle me-1"></i>
                        Terdapat data yang tidak valid. Silakan periksa kembali
                        file Excel Anda.
                      </p>
                    </div>
                  )}
                </Alert>

                {detailData.notes && detailData.notes.length > 0 && (
                  <div className="mt-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h6 className="mb-0">
                        <i className="ri-file-list-3-line align-middle me-2 text-primary"></i>
                        Detail Data Upload
                      </h6>
                      <span className="badge bg-primary-subtle text-primary">
                        {detailData.notes.length} Total Data
                      </span>
                    </div>

                    <div className="list-group list-group-flush">
                      {getDisplayedData().map((item: any, index: number) => {
                        const isValid = !item.note || item.note === "";
                        return (
                          <div
                            key={index}
                            className={`list-group-item list-group-item-action border rounded mb-2 ${
                              isValid
                                ? "border-success-subtle"
                                : "border-danger-subtle"
                            }`}
                          >
                            <div className="d-flex w-100 justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-2">
                                  <div
                                    className={`avatar-xs rounded-circle ${
                                      isValid
                                        ? "bg-success-subtle"
                                        : "bg-danger-subtle"
                                    } d-flex align-items-center justify-content-center me-2`}
                                  >
                                    <span
                                      className={`${
                                        isValid ? "text-success" : "text-danger"
                                      } fs-6 fw-bold`}
                                    >
                                      {index + 1}
                                    </span>
                                  </div>
                                  <div>
                                    <h6 className="mb-0 fw-semibold">
                                      Baris {item.row}
                                    </h6>
                                    <small className="text-muted">
                                      <i className="ri-file-excel-2-line align-middle me-1"></i>
                                      Row ke-{item.row} dari file Excel
                                    </small>
                                  </div>
                                </div>

                                {item.note && (
                                  <div className="mt-2 ms-5">
                                    <div className="alert alert-danger py-2 px-3 mb-0 small">
                                      <i className="ri-error-warning-line align-middle me-1"></i>
                                      <strong>Error:</strong> {item.note}
                                    </div>
                                  </div>
                                )}

                                {!item.note && (
                                  <div className="mt-2 ms-5">
                                    <p className="mb-0 text-success small">
                                      <i className="ri-checkbox-circle-line align-middle me-1"></i>
                                      Data berhasil diproses
                                    </p>
                                  </div>
                                )}
                              </div>

                              <div className="ms-3">
                                {isValid ? (
                                  <div className="text-center">
                                    <div className="avatar-sm rounded-circle bg-success-subtle d-flex align-items-center justify-content-center">
                                      <i className="ri-checkbox-circle-fill text-success fs-4"></i>
                                    </div>
                                    <small className="text-success fw-medium d-block mt-1">
                                      Valid
                                    </small>
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <div className="avatar-sm rounded-circle bg-danger-subtle d-flex align-items-center justify-content-center">
                                      <i className="ri-close-circle-fill text-danger fs-4"></i>
                                    </div>
                                    <small className="text-danger fw-medium d-block mt-1">
                                      Invalid
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {hasMoreData() && (
                      <div className="text-center mt-3">
                        <button
                          type="button"
                          className="btn btn-soft-primary btn-sm waves-effect waves-light"
                          onClick={handleShowMore}
                        >
                          <i className="ri-arrow-down-s-line align-middle me-1"></i>
                          Tampilkan Lebih Banyak
                          <span className="badge bg-primary ms-2">
                            {detailData.notes.length - displayCount}
                          </span>
                        </button>
                      </div>
                    )}

                    {!hasMoreData() && detailData.notes.length > 5 && (
                      <div className="text-center mt-3">
                        <div className="d-inline-flex align-items-center text-muted">
                          <i className="ri-check-double-line align-middle me-1"></i>
                          <small>
                            Menampilkan semua {detailData.notes.length} data
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          className="btn btn-soft-danger waves-effect waves-light material-shadow-none btn-sm"
          onClick={handleClose}
          disabled={isLoading}
        >
          {detailData ? "Tutup" : "Batal"}
        </button>
        {!detailData && (
          <button
            type="submit"
            className="btn btn-soft-success waves-effect waves-light material-shadow-none btn-sm"
            onClick={uploadData}
            disabled={!file || isLoading}
          >
            {isLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Uploading...
              </>
            ) : (
              <>
                <i className="ri-upload-2-line align-middle me-1"></i>
                Upload
              </>
            )}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
};

UploadPayslip.propTypes = {
  onCloseClick: PropTypes.func,
  show: PropTypes.any,
  getData: PropTypes.func,
};

export default UploadPayslip;
